import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@social-solutions/component-library';
import Card from 'components/Generics/Card/Card';
import StyledReactTooltip from 'components/Generics/ReactTooltip/StyledReactTooltip';
import { IntegrationType } from 'helpers/integration/IntegrationType';
import { AuthenticationType, TenantType } from 'models/schema-generated-models/api-response-bodies/login-options-response';
import { LoginOptions as ILoginOptions } from 'models/schema-generated-models/auth-configs/login-options';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import {  ClientFactory, LoginOptionsService } from 'services';
import { AnalyticsEvents, AnalyticsServiceFactory } from 'services/analytics-service';
import theme from 'theme';
import * as S from './LoginOptionsTabContent.styles';
import {
    SSOAPINetworkError,
    SSOAPINotFoundError,
    SSOAPIServerError,
    SSOAPIUserSessionInvalidError,
    SSOAPIUserInsufficientPermissionsError,
    SSOAPIBadRequest,
    SSOAPIUnexpectedResponseBody
} from 'services/errors';
import React from 'react';
import { t } from 'i18next';
import { NotificationService } from 'services/notification-service';
import LoadingIndicator from 'components/Generics/Loader/LoadingIndicator';
import { IntegrationContext } from 'helpers/integration/IntegrationContext';

const LoginOptionsTabContent = () => {
    const integration = useContext(IntegrationContext);

    const defaultConfig: ILoginOptions = useMemo(() => ({
        tenant_id: integration.getTenantId(),
        tenant_type: integration.getIntegrationType() === IntegrationType.Apricot ? TenantType.Apricot : TenantType.Eto,
        authentication_type: AuthenticationType.StandardAndSsoLogin,
        exclude_tenant_admins: true,
        exclusion_types: [],
    }), [integration]);

    const [isEditable, setIsEditable] = useState(false);
    const [processingRequest, setProcessingRequest] = useState(false);
    const [initialConfig, setInitialConfig] = useState<ILoginOptions>(defaultConfig);
    const [tempConfigChanges, setTempConfigChanges] = useState<ILoginOptions>(defaultConfig);
    const [loading, setLoading] = useState(true);
    const analytics = useMemo(() => AnalyticsServiceFactory.get(), []);
    const client = useMemo(() => ClientFactory.createClient(integration.getTenantId(), integration.getIntegrationType(), integration.getSessionHeaders()), [integration]);
    const loginOptionsService = useMemo(() => new LoginOptionsService(client), [client]);
    const updateError = React.useCallback((message: string) => {
        NotificationService.getInstance().notifyError(message);
    }, []);
    const productAdminType = integration.getProductAdminType();

    useEffect(() => {
        const fetchConfig = async () => {
            setLoading(true);
            let config: ILoginOptions;
            try {
                config = await loginOptionsService.getLoginOptions();
                setInitialConfig(config);
                setTempConfigChanges(config);
                NotificationService.getInstance().notifyInfo(t('notifications:NOTIFICATION_LOGIN_OPTIONS_LOAD_SUCCESSFUL'));
            } catch (error) {
                if (error instanceof SSOAPINotFoundError) {
                    config = await loginOptionsService.postLoginOptions(defaultConfig);
                    setInitialConfig(config);
                    setTempConfigChanges(config);
                } else if (error instanceof SSOAPIBadRequest) {
                    updateError(t('notifications:NOTIFICATION_API_ERROR_BAD_REQUEST'));
                } else if (error instanceof SSOAPINetworkError) {
                    updateError(t('notifications:NOTIFICATION_API_ERROR_NETWORK_ERROR'));
                } else if (error instanceof SSOAPIUserSessionInvalidError) {
                    updateError(t('notifications:NOTIFICATION_API_ERROR_USER_SESSION_INVALID', { PRODUCT: defaultConfig.tenant_type }));
                } else if (error instanceof SSOAPIUserInsufficientPermissionsError) {
                    updateError(t('notifications:NOTIFICATION_API_ERROR_USER_INSUFFICIENT_PERMISSION', { PRODUCT_ADMIN_TYPE: productAdminType }));
                } else if (error instanceof SSOAPIServerError) {
                    updateError(t('notifications:NOTIFICATION_API_ERROR_GENERIC_SERVER_ERROR', { transaction_id: error.getAPIContext().eventId}));
                } else if (error instanceof SSOAPIUnexpectedResponseBody) {
                    updateError(t('notifications:NOTIFICATION_API_ERROR_UNEXPECTED_RESPONSE_BODY'));
                } else {
                    updateError(t('notifications:NOTIFICATION_API_ERROR_UNEXPECTED_ERROR'));
                }
            } finally {
                setLoading(false);
            }
        };

        fetchConfig();
    }, [defaultConfig, loginOptionsService, productAdminType, updateError]);

    const handleSave = async () => {
        setProcessingRequest(true);
        let updatedConfig: ILoginOptions;
        const tempConfigChagnesCopy = { ...tempConfigChanges };
        try {
            updatedConfig = await loginOptionsService.putLoginOptions(tempConfigChanges);
            setInitialConfig(updatedConfig);
            setIsEditable(false);
            NotificationService.getInstance().notifySuccess(t('notifications:NOTIFICATION_LOGIN_OPTIONS_SAVE_SUCCESSFUL'));
        } catch (error) {
            if (error instanceof SSOAPINetworkError) {
                return updateError(t('notifications:NOTIFICATION_API_ERROR_NETWORK_ERROR'));
            } else if (error instanceof SSOAPIUserSessionInvalidError) {
                return updateError(t('notifications:NOTIFICATION_API_ERROR_USER_SESSION_INVALID', { PRODUCT: defaultConfig.tenant_type }));
            } else if (error instanceof SSOAPIUserInsufficientPermissionsError) {
                return updateError(t('notifications:NOTIFICATION_API_ERROR_USER_INSUFFICIENT_PERMISSION', { PRODUCT_ADMIN_TYPE: productAdminType }));
            } else if (error instanceof SSOAPIServerError) {
                return updateError(t('notifications:NOTIFICATION_API_ERROR_GENERIC_SERVER_ERROR', { transaction_id: error.getAPIContext().eventId}));
            } else {
                updateError(t('notifications:NOTIFICATION_API_ERROR_UNEXPECTED_ERROR'));
            }
        } finally {
            setProcessingRequest(false);
            analytics.track(AnalyticsEvents.LOGIN_OPTIONS_SAVED, {
                authentication_type: tempConfigChagnesCopy.authentication_type
            });
        }
    };

    const handleCancel = () => {
        setIsEditable(false);
        setTempConfigChanges(initialConfig);
    };

    const toggleAuthenticationType = (isChecked: boolean) => {
        const newAuthType = isChecked ? AuthenticationType.SsoLoginOnly : AuthenticationType.StandardAndSsoLogin;
        setTempConfigChanges(currentConfig => ({ ...currentConfig, authentication_type: newAuthType }));
    };

    return (
        <Card title={<Trans>LOGIN.TITLE</Trans>}>
            {loading ? (
                <LoadingIndicator />
            ) : (
                <>
                    <S.CheckboxContainer>
                        <S.Checkbox
                            id="forceSsoLoginCheckbox"
                            checked={tempConfigChanges.authentication_type === AuthenticationType.SsoLoginOnly}
                            onChange={(e) => toggleAuthenticationType(e.target.checked)}
                            disabled={!isEditable || processingRequest}
                        />
                        <S.Label htmlFor="forceSsoLoginCheckbox" active={isEditable}>
                            Force SSO Login for All Users
                        </S.Label>
                        {isEditable && (
                            <>
                                <S.ToolTipIcon
                                    icon={faQuestionCircle}
                                    data-tip="Selecting this option will block any access to<br>standard login options for all non-admin users."
                                    data-multiline={true}
                                />
                                <StyledReactTooltip
                                    backgroundColor={theme.sherpaBlue}
                                    textColor={theme.white}
                                    place="right"
                                />
                            </>
                        )}
                    </S.CheckboxContainer>
                    {!isEditable ? (
                        <Button variant='outlined' onClick={() => setIsEditable(true)} disabled={processingRequest}>Update</Button>
                    ) : (
                        <>
                            <S.ButtonContainer>
                                <Button variant='outlined' onClick={handleCancel} disabled={!isEditable || processingRequest}><Trans>BUTTON.LABEL_CANCEL</Trans></Button>
                                <Button onClick={handleSave} disabled={!isEditable || processingRequest}><Trans>BUTTON.LABEL_SAVE</Trans></Button>
                            </S.ButtonContainer>
                        </>
                    )}
                </>
            )}
        </Card>
    );
};

export default LoginOptionsTabContent;
